import React, { useEffect, useMemo, useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd/es/menu';
import { useSimpleModal } from '../../Modal/Simple';
import { CommentData, useCommentDelete, useCommentHide, useCommentRestore } from '../../../../hooks/api/comments';
import PostOrCommentTemplate from '../../PostOrCommentTemplate';
import ReasonModal from '../../ReasonModal';

export interface CommentProps {
  data: CommentData;
  setPage: (page: number) => void;
}

export default function SubComment({ data, setPage }: CommentProps): JSX.Element {
  const navigate = useNavigate();
  const { modal, contextHolder } = useSimpleModal();
  const [openHideComment, setOpenHideComment] = useState(false);
  const commentDelete = useCommentDelete(data.id);
  const commentHide = useCommentHide();
  const commentRestore = useCommentRestore();

  useEffect(() => {
    if (commentDelete.data && !commentDelete.error && !commentDelete.loading) {
      setPage(1);
    }
  }, [commentDelete.data]);

  useEffect(() => {
    if (commentHide.data && !commentHide.error && !commentHide.loading) {
      setOpenHideComment(false);
      setPage(1);
    }
  }, [commentHide.data]);

  useEffect(() => {
    if (commentRestore.data && !commentRestore.error && !commentRestore.loading) {
      setPage(1);
    }
  }, [commentRestore.data]);

  const confirmDelete = () => {
    if (data.id) {
      modal.confirm({
        title: 'Are you sure you want to Delete the comment?',
        icon: <ExclamationCircleOutlined />,
        onOk: () => commentDelete.fetch(),
        okText: 'Delete',
      });
    }
  };

  const hideComment = (reason: string) => {
    commentHide.fetch({ reason }, `${data.id}/hide`);
  };

  const confirmRestore = () => {
    modal.confirm({
      title: 'Are you sure you want to Publish the comment?',
      icon: <ExclamationCircleOutlined />,
      onOk: () => commentRestore.fetch(undefined, `${data.id}/restore`),
      okText: 'Publish',
    });
  };

  const dropdownMenu: MenuProps = useMemo(
    () => ({
      items: [
        {
          key: '1',
          label: 'Edit',
          onClick: () => navigate(`/comments/${data.id}/update`),
        },
        {
          key: '2',
          label: 'Delete',
          onClick: confirmDelete,
        },
        !data.isHidden
          ? {
            key: '3',
            label: 'Hide',
            onClick: () => setOpenHideComment(true),
          }
          : {
            key: '3',
            label: 'Publish',
            onClick: confirmRestore,
          },
      ],
    }),
    [data],
  );

  return (
    <>
      <PostOrCommentTemplate data={data} type="subComments" dropdownMenu={dropdownMenu} />
      <ReasonModal
        open={openHideComment}
        setOpen={setOpenHideComment}
        title="Hide comment?"
        actionButtonText="Hide comment"
        placeholder="Why is this comment being hidden?"
        subtitle="The comment will no longer be visible to other users."
        loading={commentHide.loading}
        onSubmit={hideComment}
      />
      {contextHolder}
    </>
  );
}
